/**
 * seenBy — who has read what in a thread, from receipts (2026-09-08, PR-3).
 * Pure: the host renders the result; nothing here touches Firestore.
 *
 * A READER is a participant uid or `'assistant'` (Prest0n's server-written
 * receipt — never a uid, so it can never collide). A receipt is a high-water
 * mark: `lastReadOrderTs` — the reader has read every message at or below it.
 *
 * Two questions the UI asks:
 *   • `seenByRows` — for each reader, WHICH message carries their glyph: the
 *     LAST message in transcript order at or below their mark that they did
 *     not send themselves (a reader never "sees" their own message). The
 *     viewer's own receipt is never rendered (they know what they read).
 *   • `readersOf` — for one message, who has read it (the accent ✓✓ in a
 *     direct message, the tooltip list).
 *
 * A message's order is its `orderTs` (the shared-store key); a local bubble
 * without one falls back to its timestamp — same instant, same clock.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';

export const ASSISTANT_RECEIPT_ID = 'assistant';

export interface ReceiptFacts {
  /** The reader has read every message with orderTs <= this. */
  lastReadOrderTs: number;
  lastReadMessageId?: string;
}

/** reader id (uid or 'assistant') → their receipt. */
export type ReceiptsByReader = Record<string, ReceiptFacts>;

type MessageLike = Pick<ChatMessage, 'id' | 'isUser' | 'senderId' | 'orderTs' | 'timestamp' | 'isSystem'>;

/** The instant a message orders by: `orderTs`, else its timestamp; null when undatable. */
export function messageOrderTs(m: MessageLike | null | undefined): number | null {
  if (!m) return null;
  if (typeof m.orderTs === 'number' && Number.isFinite(m.orderTs)) return m.orderTs;
  const t = m.timestamp as unknown;
  if (t instanceof Date) {
    const ms = t.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof t === 'string' || typeof t === 'number') {
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

/** Who sent a message, as a reader id: the sender uid, 'assistant' for Prest0n, '' when unknown. */
export function messageAuthorId(m: MessageLike | null | undefined): string {
  if (!m) return '';
  if (m.isUser !== true) return ASSISTANT_RECEIPT_ID;
  return typeof m.senderId === 'string' ? m.senderId.trim() : '';
}

function validReceipt(r: ReceiptFacts | undefined): r is ReceiptFacts {
  return !!r && typeof r.lastReadOrderTs === 'number' && Number.isFinite(r.lastReadOrderTs) && r.lastReadOrderTs >= 0;
}

export interface SeenByOptions {
  /** The viewer — their own receipt is never rendered. */
  selfUid: string | null | undefined;
  /**
   * Readers allowed to appear: the thread's human participants. `'assistant'`
   * is always admitted unless `assistantEnabled === false`. When omitted, every
   * receipt in the map counts (the store is participant-gated server-side).
   */
  participantIds?: ReadonlyArray<string> | null;
  assistantEnabled?: boolean;
}

function admittedReaders(receipts: ReceiptsByReader, opts: SeenByOptions): string[] {
  const self = (opts.selfUid ?? '').trim();
  const allow = opts.participantIds ? new Set(opts.participantIds.map((p) => String(p ?? '').trim()).filter(Boolean)) : null;
  const out: string[] = [];
  for (const [reader, facts] of Object.entries(receipts ?? {})) {
    const id = (reader ?? '').trim();
    if (!id || id === self) continue;
    if (!validReceipt(facts)) continue;
    if (id === ASSISTANT_RECEIPT_ID) {
      if (opts.assistantEnabled === false) continue;
    } else if (allow && !allow.has(id)) {
      continue;
    }
    out.push(id);
  }
  return out;
}

/** Readers whose receipt covers `message` (never its author, never the viewer). */
export function readersOf(
  message: MessageLike | null | undefined,
  receipts: ReceiptsByReader,
  opts: SeenByOptions,
): string[] {
  const at = messageOrderTs(message);
  if (at === null || !message || message.isSystem) return [];
  const author = messageAuthorId(message);
  return admittedReaders(receipts, opts).filter((r) => r !== author && receipts[r].lastReadOrderTs >= at);
}

/**
 * message id → readers whose glyph sits under it: for each reader, the last
 * message (transcript order) at or below their mark that they did not author.
 * Messages without a usable id or instant, and system rows, never carry one.
 */
export function seenByRows(
  messages: ReadonlyArray<MessageLike>,
  receipts: ReceiptsByReader,
  opts: SeenByOptions,
): Map<string, string[]> {
  const rows = new Map<string, string[]>();
  const readers = admittedReaders(receipts, opts);
  if (readers.length === 0 || !Array.isArray(messages) || messages.length === 0) return rows;
  for (const reader of readers) {
    const mark = receipts[reader].lastReadOrderTs;
    let target: string | null = null;
    for (const m of messages) {
      if (!m || m.isSystem) continue;
      const id = typeof m.id === 'string' ? m.id.trim() : '';
      if (!id) continue;
      const at = messageOrderTs(m);
      if (at === null || at > mark) continue;
      if (messageAuthorId(m) === reader) continue;
      target = id; // later messages in transcript order replace earlier ones
    }
    if (!target) continue;
    const list = rows.get(target) ?? [];
    list.push(reader);
    rows.set(target, list);
  }
  return rows;
}

/**
 * The receipt the viewer writes after dwelling on a thread: the newest
 * datable, non-system message in the transcript (their own included — they
 * certainly saw those). Null when nothing is datable.
 */
export function receiptForTranscript(messages: ReadonlyArray<MessageLike>): ReceiptFacts | null {
  let best: { at: number; id: string } | null = null;
  for (const m of messages ?? []) {
    if (!m || m.isSystem) continue;
    const at = messageOrderTs(m);
    if (at === null) continue;
    const id = typeof m.id === 'string' ? m.id.trim() : '';
    if (!best || at >= best.at) best = { at, id };
  }
  if (!best) return null;
  return best.id ? { lastReadOrderTs: best.at, lastReadMessageId: best.id } : { lastReadOrderTs: best.at };
}

/**
 * Prest0n's receipt from the server's side (parity helper — the server stamps
 * `lastReadOrderTs = the human anchor's orderTs`): every human message at or
 * before the turn Prest0n answered has been read by Prest0n.
 */
export function isReadByAssistant(message: MessageLike | null | undefined, receipts: ReceiptsByReader): boolean {
  const r = receipts?.[ASSISTANT_RECEIPT_ID];
  const at = messageOrderTs(message);
  if (!validReceipt(r) || at === null || !message || message.isUser !== true) return false;
  return r.lastReadOrderTs >= at;
}
